import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Switch,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  useTheme,
  useMediaQuery,
  Skeleton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store/store';
import { AIInstruction } from '../../types/aiInstruction';
import { toggleKeywordActive, deleteKeyword } from '../../store/slices/aiInstructionSlice';
import KeywordDeleteConfirm from './KeywordDeleteConfirm';
import { formatDistanceToNow } from 'date-fns';
import { cs } from 'date-fns/locale';
import { useMobileLayout } from '../../hooks/useMobileLayout';

interface KeywordListProps {
  keywords: AIInstruction[];
  loading: boolean;
  onEdit: (id: number) => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const KeywordList: React.FC<KeywordListProps> = ({
  keywords,
  loading,
  onEdit,
  onSuccess,
  onError,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isSmallMobile, spacing } = useMobileLayout();

  const [search, setSearch] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedKeywordId, setSelectedKeywordId] = useState<number | null>(null);

  const handleToggleActive = async (id: number) => {
    try {
      await dispatch(toggleKeywordActive(id)).unwrap();
      onSuccess('Stav klíčového slova byl změněn');
    } catch (error: any) {
      onError(error || 'Nepodařilo se změnit stav klíčového slova');
    }
  };

  const handleDeleteClick = (id: number) => {
    setSelectedKeywordId(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedKeywordId) {
      try {
        await dispatch(deleteKeyword(selectedKeywordId)).unwrap();
        onSuccess('Klíčové slovo bylo smazáno');
        setDeleteConfirmOpen(false);
        setSelectedKeywordId(null);
      } catch (error: any) {
        onError(error || 'Nepodařilo se smazat klíčové slovo');
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setSelectedKeywordId(null);
  };

  const filteredKeywords = keywords.filter((k) =>
    k.keyword.toLowerCase().includes(search.toLowerCase()) ||
    k.aiInstructions.toLowerCase().includes(search.toLowerCase())
  );

  const getPriorityColor = (priority: number) => {
    if (priority >= 75) return 'error';
    if (priority >= 50) return 'warning';
    return 'info';
  };

  if (loading) {
    return (
      <Box sx={{ p: spacing.sm }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  // Mobile Card View
  if (isMobile) {
    return (
      <Box sx={{ p: spacing.sm }}>
        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder="Vyhledat klíčové slovo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: spacing.sm }}
        />

        {filteredKeywords.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
            {keywords.length === 0 ? 'Zatím nemáte žádná klíčová slova' : 'Žádné výsledky'}
          </Typography>
        ) : (
          filteredKeywords.map((keyword) => (
            <Card key={keyword.id} sx={{ mb: spacing.sm }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" fontWeight="bold">
                    {keyword.keyword}
                  </Typography>
                  <Switch
                    checked={keyword.isActive}
                    onChange={() => handleToggleActive(keyword.id)}
                    size="small"
                  />
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {keyword.aiInstructions}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  <Chip
                    label={`Priorita: ${keyword.priority}`}
                    color={getPriorityColor(keyword.priority)}
                    size="small"
                  />
                  <Chip
                    label={`Použito: ${keyword.usageCount}×`}
                    size="small"
                    variant="outlined"
                  />
                  {keyword.caseSensitive && (
                    <Chip label="Rozlišuje velikost" size="small" variant="outlined" />
                  )}
                  {keyword.matchWholeWord && (
                    <Chip label="Celé slovo" size="small" variant="outlined" />
                  )}
                </Box>

                {keyword.lastMatchedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Naposledy použito:{' '}
                    {formatDistanceToNow(new Date(keyword.lastMatchedAt), {
                      addSuffix: true,
                      locale: cs,
                    })}
                  </Typography>
                )}
              </CardContent>

              <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                <IconButton size="small" onClick={() => onEdit(keyword.id)} color="primary">
                  <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => handleDeleteClick(keyword.id)} color="error">
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          ))
        )}

        <KeywordDeleteConfirm
          open={deleteConfirmOpen}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          keyword={keywords.find((k) => k.id === selectedKeywordId)?.keyword || ''}
        />
      </Box>
    );
  }

  // Desktop Table View
  return (
    <Box sx={{ p: spacing.sm }}>
      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Vyhledat klíčové slovo nebo instrukce..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: search && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearch('')}>
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: spacing.sm }}
      />

      {filteredKeywords.length === 0 ? (
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
          {keywords.length === 0 ? 'Zatím nemáte žádná klíčová slova' : 'Žádné výsledky'}
        </Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Aktivní</TableCell>
                <TableCell>Klíčové slovo</TableCell>
                <TableCell>AI Instrukce</TableCell>
                <TableCell align="center">Priorita</TableCell>
                <TableCell align="center">Použití</TableCell>
                <TableCell>Nastavení</TableCell>
                <TableCell align="right">Akce</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredKeywords.map((keyword) => (
                <TableRow key={keyword.id} hover>
                  <TableCell>
                    <Switch
                      checked={keyword.isActive}
                      onChange={() => handleToggleActive(keyword.id)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {keyword.keyword}
                    </Typography>
                    {keyword.lastMatchedAt && (
                      <Typography variant="caption" color="text.secondary">
                        {formatDistanceToNow(new Date(keyword.lastMatchedAt), {
                          addSuffix: true,
                          locale: cs,
                        })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={keyword.aiInstructions} arrow>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {keyword.aiInstructions}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={keyword.priority}
                      color={getPriorityColor(keyword.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={`${keyword.usageCount}×`} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {keyword.caseSensitive && (
                        <Chip label="Case" size="small" variant="outlined" />
                      )}
                      {keyword.matchWholeWord && (
                        <Chip label="Whole" size="small" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => onEdit(keyword.id)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(keyword.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <KeywordDeleteConfirm
        open={deleteConfirmOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        keyword={keywords.find((k) => k.id === selectedKeywordId)?.keyword || ''}
      />
    </Box>
  );
};

export default KeywordList;

